import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Title1,
  Badge,
  Card,
  CardHeader,
  Button,
  Divider,
  tokens,
} from '@fluentui/react-components';
import {
  NoteRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  NewRegular,
  WrenchRegular,
  BugRegular,
  RocketRegular,
} from '@fluentui/react-icons';

// ──────────────────────────────────────────────
//  ADD NEW PATCH NOTES AT THE TOP OF THIS ARRAY
// ──────────────────────────────────────────────
//
// "No default" ruling (2026-10-21) / forward-only release notes: entries written BEFORE this
// bilingual mechanism existed stay plain English strings (`text: string`) -- this is not
// retroactively translated. Every entry from here forward should use `{ en, es }` instead, so a
// Spanish-toggled reader sees real Spanish for current/future releases, never a half-translated
// history. localizedChangeText() below picks the right one, falling back to English for the
// plain-string legacy shape.

interface PatchNote {
  version: string;
  date: string;
  title: string | { en: string; es: string };
  changes: { type: 'feature' | 'fix' | 'improvement'; text: string | { en: string; es: string } }[];
}

/** Resolves a title or a change's text for the current language. Legacy plain-string entries (written
 *  before the {en,es} shape existed) are English-only by the "no default" / forward-only ruling. */
function localizedText(text: string | { en: string; es: string }, lang: string): string {
  if (typeof text === 'string') return text;
  return lang.startsWith('es') ? text.es : text.en;
}

const patchNotes: PatchNote[] = [
  {
    version: '1.32.0',
    date: 'November 3, 2026',
    title: { en: 'Sort any column, and group Tax Returns by scheduled date', es: 'Ordene cualquier columna, y agrupe Declaraciones de Impuestos por fecha programada' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Every sortable column on Client Outreach and Tax Returns now has its own menu (the small arrow next to the column name) with explicit "Sort A to Z" and "Sort Z to A" options.',
          es: 'Cada columna ordenable en Contacto con Clientes y Declaraciones de Impuestos ahora tiene su propio menú (la pequeña flecha junto al nombre de la columna) con opciones explícitas de "Ordenar de A a Z" y "Ordenar de Z a A".',
        },
      },
      {
        type: 'feature',
        text: {
          en: 'Tax Returns can now group rows by Scheduled Date — Year, Quarter, Month, Week, or Day — from that same column menu. Groups show a count and can be collapsed.',
          es: 'Declaraciones de Impuestos ahora puede agrupar filas por Fecha Programada — Año, Trimestre, Mes, Semana o Día — desde ese mismo menú de columna. Los grupos muestran un total y se pueden contraer.',
        },
      },
    ],
  },
  {
    version: '1.31.0',
    date: 'November 2, 2026',
    title: { en: 'Saved views and your filters now follow you to any computer', es: 'Las vistas guardadas y sus filtros ahora lo siguen a cualquier computadora' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Client Outreach and Tax Returns now remember your filters across devices: the view you land on, and any views you save, are tied to your staff account — not to one browser or one computer. Open the app on the front-desk machine and your saved views are right there.',
          es: 'Contacto con Clientes y Declaraciones de Impuestos ahora recuerdan sus filtros en todos los dispositivos: la vista en la que aterriza, y cualquier vista que guarde, está vinculada a su cuenta de personal — no a un navegador o una computadora. Abra la aplicación en la computadora de recepción y sus vistas guardadas estarán allí.',
        },
      },
      {
        type: 'improvement',
        text: {
          en: 'Shared views actually share now: a view you mark "Share with team" is visible to your teammates on the Views menu, not just on your own device.',
          es: 'Las vistas compartidas ahora realmente se comparten: una vista que marque "Compartir con el equipo" es visible para sus compañeros en el menú de Vistas, no solo en su propio dispositivo.',
        },
      },
    ],
  },
  {
    version: '1.30.0',
    date: 'November 2, 2026',
    title: { en: 'Smarter filters, and a real Preparer Roster', es: 'Filtros más inteligentes, y una Cartera por Preparador real' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Filtering by Preparer, Last Preparer, Status, or Type is now one searchable list: type to narrow, check the ones you want, see how many rows each one matches, and optionally flip "Exclude these" or filter for "Has no value."',
          es: 'Filtrar por Preparador, Último Preparador, Estado o Tipo ahora es una sola lista con búsqueda: escriba para reducir, marque los que desee, vea cuántas filas coincide cada uno, y opcionalmente active "Excluir estos" o filtre por "Sin valor."',
        },
      },
      {
        type: 'improvement',
        text: {
          en: 'The Preparer Roster is now a full list: choose which columns to show (email, phone, SMS-capable, type, in-house, client language, and more), select several preparers at once, and the Excel export matches exactly the columns you chose, in order.',
          es: 'La Cartera por Preparador ahora es una lista completa: elija qué columnas mostrar (correo electrónico, teléfono, capacidad de SMS, tipo, interno, idioma del cliente y más), seleccione varios preparadores a la vez, y la exportación a Excel coincide exactamente con las columnas elegidas, en orden.',
        },
      },
    ],
  },
  {
    version: '1.29.0',
    date: 'November 2, 2026',
    title: { en: 'Drake Upload: fixed Match also opening the wrong dialog', es: 'Carga Drake: se corrigió que Match también abriera el diálogo equivocado' },
    changes: [
      {
        type: 'fix',
        text: {
          en: 'Fixed a mix-up in Drake Upload\'s Pending Review tab: clicking "Match" on a document could also pop open that row\'s Detail dialog at the same time.',
          es: 'Se corrigió una confusión en la pestaña de Revisión Pendiente de Carga Drake: hacer clic en "Match" en un documento también podía abrir el diálogo de Detalle de esa fila al mismo tiempo.',
        },
      },
    ],
  },
  {
    version: '1.28.0',
    date: 'November 2, 2026',
    title: { en: 'Client Outreach: fixed a freeze when selecting many clients at once', es: 'Contacto con Clientes: se corrigió un congelamiento al seleccionar muchos clientes a la vez' },
    changes: [
      {
        type: 'fix',
        text: {
          en: 'Fixed a freeze in Client Outreach: clicking "Select All Matching" could lock up the page, requiring a reload.',
          es: 'Se corrigió un congelamiento en Contacto con Clientes: hacer clic en "Seleccionar Todos los Coincidentes" podía bloquear la página, requiriendo recargarla.',
        },
      },
    ],
  },
  {
    version: '1.27.0',
    date: 'November 1, 2026',
    title: { en: 'Client Outreach: keyboard search, save your views, and more', es: 'Contacto con Clientes: búsqueda por teclado, guarde sus vistas y más' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Press "/" anywhere on the Client Outreach screen to jump straight to search. Your search and filters now stay in the page\'s link, so sharing or bookmarking it keeps what you had set up.',
          es: 'Presione "/" en cualquier parte de la pantalla de Contacto con Clientes para saltar directamente a la búsqueda. Su búsqueda y filtros ahora se mantienen en el enlace de la página, así que compartirlo o guardarlo como favorito conserva lo que tenía configurado.',
        },
      },
      {
        type: 'feature',
        text: {
          en: 'You can now select every client matching your current filters, not just the ones on the page — and every send now shows you a preview first, no exceptions.',
          es: 'Ahora puede seleccionar a todos los clientes que coincidan con sus filtros actuales, no solo los de la página — y cada envío ahora le muestra una vista previa primero, sin excepciones.',
        },
      },
      {
        type: 'feature',
        text: {
          en: 'Save your own named views (filters + search) with the new "Views" button, mark one as your default, and pick which columns show with the new column chooser.',
          es: 'Guarde sus propias vistas con nombre (filtros + búsqueda) con el nuevo botón "Vistas", marque una como su predeterminada, y elija qué columnas se muestran con el nuevo selector de columnas.',
        },
      },
    ],
  },
  {
    version: '1.26.0',
    date: 'November 1, 2026',
    title: { en: 'See who last prepared each client\'s return, by year', es: 'Vea quién preparó por última vez la declaración de cada cliente, por año' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Client Outreach now has a "Last Preparer" column and filter, plus a year picker so you can see who prepared each client\'s return in any past year — not just their latest one.',
          es: 'Contacto con Clientes ahora tiene una columna y filtro de "Último Preparador", además de un selector de año para ver quién preparó la declaración de cada cliente en cualquier año anterior — no solo el más reciente.',
        },
      },
      {
        type: 'feature',
        text: {
          en: 'A new "Preparer Roster" view shows every client a chosen preparer did in a chosen year, with a one-click export to Excel.',
          es: 'Una nueva vista de "Cartera por Preparador" muestra a todos los clientes que un preparador elegido atendió en un año elegido, con exportación a Excel con un clic.',
        },
      },
    ],
  },
  {
    version: '1.25.0',
    date: 'November 1, 2026',
    title: { en: 'Search now finds accented names and phone numbers in any format', es: 'La búsqueda ahora encuentra nombres con acentos y números de teléfono en cualquier formato' },
    changes: [
      {
        type: 'improvement',
        text: {
          en: 'Search boxes across the app now find a name regardless of accents — searching "jose" finds "José" — and find a phone number no matter how it\'s formatted, so "2395550111" finds "(239) 555-0111". The first match in each result is now highlighted.',
          es: 'Los cuadros de búsqueda en toda la aplicación ahora encuentran un nombre sin importar los acentos — buscar "jose" encuentra a "José" — y encuentran un número de teléfono sin importar su formato, así que "2395550111" encuentra "(239) 555-0111". La primera coincidencia en cada resultado ahora aparece resaltada.',
        },
      },
    ],
  },
  {
    version: '1.24.0',
    date: 'November 1, 2026',
    title: { en: 'Fix: Staff Roles page could freeze the browser tab', es: 'Corregido: la página de Roles de Personal podía congelar la pestaña del navegador' },
    changes: [
      {
        type: 'fix',
        text: {
          en: 'Fixed a bug that could make the Staff Roles page render in an endless loop, freezing that browser tab. The page now loads normally every time.',
          es: 'Se corrigió un error que podía hacer que la página de Roles de Personal se renderizara en un bucle infinito, congelando esa pestaña del navegador. La página ahora carga normalmente siempre.',
        },
      },
    ],
  },
  {
    version: '1.23.0',
    date: 'November 1, 2026',
    title: { en: 'Filtering, quick views, and match counts on Client Outreach', es: 'Filtrado, vistas rápidas y conteos de coincidencias en Contacto con Clientes' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Client Outreach screen now has the same per-column filters as the Tax Returns list (click the funnel on a column header), plus five one-click quick views: All, My Clients, Not Contacted, In-House Pending, and Last Filed Before 2025. A count above the table shows how many clients match.',
          es: 'La pantalla de Contacto con Clientes ahora tiene los mismos filtros por columna que la lista de Declaraciones de Impuestos (haga clic en el embudo del encabezado de columna), además de cinco vistas rápidas de un clic: Todos, Mis Clientes, No Contactados, En Casa Pendientes y Última Declaración Antes de 2025. Un conteo arriba de la tabla muestra cuántos clientes coinciden.',
        },
      },
      {
        type: 'feature',
        text: {
          en: 'Client type, preparer, and in-house/contacted/opted-out filters now show how many clients match each option, right in the filter menu — so you can see at a glance which choices are worth clicking.',
          es: 'Los filtros de tipo de cliente, preparador y en-casa/contactado/baja ahora muestran cuántos clientes coinciden con cada opción, directamente en el menú de filtro — para que pueda ver de un vistazo qué opciones vale la pena hacer clic.',
        },
      },
      {
        type: 'fix',
        text: {
          en: 'A client row with several badges (In-House, Drake checklist, Opted Out) no longer grows taller than its neighbors — the name shortens with "…" instead, and the badges always stay on one line.',
          es: 'Una fila de cliente con varias insignias (En Casa, lista de Drake, Baja) ya no crece más alta que sus vecinas — el nombre se acorta con "…" en su lugar, y las insignias siempre permanecen en una línea.',
        },
      },
    ],
  },
  {
    version: '1.22.0',
    date: 'November 1, 2026',
    title: { en: 'Export to Excel on the Tax Returns list', es: 'Exportar a Excel en la lista de Declaraciones de Impuestos' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Tax Returns list now has an "Export to Excel" button — it downloads exactly the rows you\'re currently viewing (filtered, with the columns you see, in the order you see them), in English or Spanish depending on your language setting. Suggested by Emma.',
          es: 'La lista de Declaraciones de Impuestos ahora tiene un botón "Exportar a Excel" — descarga exactamente las filas que está viendo actualmente (filtradas, con las columnas que ve, en el orden en que las ve), en inglés o español según su configuración de idioma. Sugerido por Emma.',
        },
      },
    ],
  },
  {
    version: '1.21.0',
    date: 'November 1, 2026',
    title: { en: 'Right-click on links, error messages that let you retry, and a visual polish pass', es: 'Clic derecho en los enlaces, mensajes de error con reintento, y un repaso visual' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Right-click, middle-click and Ctrl/Cmd-click now work on every link in the app — tax returns, clients, calendar entries, notifications, breadcrumbs, and document/Xero/Dataverse links all open in a new tab the way links do everywhere else on the web. Suggested by Helen.',
          es: 'El clic derecho, el clic central y Ctrl/Cmd-clic ahora funcionan en todos los enlaces de la aplicación — las declaraciones de impuestos, clientes, entradas del calendario, notificaciones, migas de pan y los enlaces de documentos/Xero/Dataverse ahora se abren en una pestaña nueva, como en el resto de la web. Sugerido por Helen.',
        },
      },
      {
        type: 'fix',
        text: {
          en: 'Calendar, Preparer Billing, Settings, and Staff Roles now show a clear error message with a working Retry button if their data fails to load, instead of going blank — and Settings no longer silently shows default values that could be mistaken for the real saved setting.',
          es: 'Calendario, Facturación de Preparadores, Configuración y Roles de Personal ahora muestran un mensaje de error claro con un botón de Reintentar funcional si sus datos no cargan, en lugar de quedar en blanco — y Configuración ya no muestra silenciosamente valores predeterminados que podrían confundirse con el ajuste real guardado.',
        },
      },
      {
        type: 'improvement',
        text: {
          en: 'Spacing and text sizing across the app now follow one consistent scale — a visual-consistency pass, not a layout change you should notice.',
          es: 'El espaciado y el tamaño del texto en toda la aplicación ahora siguen una escala consistente — un repaso de consistencia visual, no un cambio de diseño que deba notar.',
        },
      },
    ],
  },
  {
    version: '1.20.0',
    date: 'October 29, 2026',
    title: { en: 'Notification History, Patch Notes, and Client Opt-Outs in Spanish', es: 'Historial de Notificaciones, Notas de Versión y Bajas de Clientes en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Three more screens now follow the language toggle — the Notification History panel (labels, the send-notification dialog), the Client Opt-Outs page (stats, filters, table, reactivation, the add-opt-out dialog), and the titles of release notes from this version onward.',
          es: 'Tres pantallas más ahora siguen el interruptor de idioma — el panel de Historial de Notificaciones (etiquetas, el diálogo de envío de notificación), la página de Bajas de Clientes (estadísticas, filtros, tabla, reactivación, el diálogo de agregar baja) y los títulos de las notas de versión a partir de esta versión.',
        },
      },
    ],
  },
  {
    version: '1.19.0',
    date: 'October 27, 2026',
    title: { en: 'Outreach, Preparer Billing & Staff Roles in Spanish', es: 'Contacto con Clientes, Facturación de Preparadores y Roles del Personal en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'Three more screens now follow the language toggle — Client Outreach (client list, outreach history, send-email dialog controls — not the email content itself), Preparer Billing (bill generation, payment recording, the Xero push), and Staff Roles (including the admin-removal safety confirmation).',
          es: 'Tres pantallas más ahora siguen el interruptor de idioma — Contacto con Clientes (lista de clientes, historial de contacto, controles del diálogo de envío de correo — no el contenido del correo en sí), Facturación de Preparadores (generación de facturas, registro de pagos, el envío a Xero) y Roles del Personal (incluyendo la confirmación de seguridad al quitar acceso de administrador).',
        },
      },
    ],
  },
  {
    version: '1.18.0',
    date: 'October 27, 2026',
    title: { en: 'Client Uploads Page in Spanish', es: 'Página de Cargas de Clientes en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Client Uploads page now follows the language toggle — general and return-linked upload tables, filters, the bulk ZIP download, and the reassign-to-return dialog. First screen translated outside the Tax Returns list and detail page; the rest of the app follows by how often staff use each screen.',
          es: 'La página de Cargas de Clientes ahora sigue el interruptor de idioma — las tablas de cargas generales y vinculadas a declaraciones, los filtros, la descarga masiva en ZIP y el diálogo de reasignación a una declaración. Primera pantalla traducida fuera de la lista y el detalle de Declaraciones de Impuestos; el resto de la aplicación sigue según el uso de cada pantalla.',
        },
      },
    ],
  },
  {
    version: '1.17.0',
    date: 'October 27, 2026',
    title: { en: 'Detail Page Fully Bilingual', es: 'Página de Detalle Completamente Bilingüe' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Activity and Workflow tabs now follow the language toggle — notes, status-change history, duplicate-return warnings, and appointment-type guidance. This finishes the tax return detail page: all five tabs plus the page\'s own header, toolbar, and dialogs are now fully bilingual.',
          es: 'Las pestañas de Actividad y Flujo de Trabajo ahora siguen el interruptor de idioma — las notas, el historial de cambios de estado, las advertencias de declaraciones duplicadas y la guía según el tipo de cita. Esto completa la página de detalle de la declaración de impuestos: las cinco pestañas además del encabezado, la barra de herramientas y los diálogos de la propia página están ahora completamente bilingües.',
        },
      },
    ],
  },
  {
    version: '1.16.0',
    date: 'October 26, 2026',
    title: { en: 'Documents Tab in Spanish', es: 'Pestaña de Documentos en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Documents tab on the tax return detail page now follows the language toggle — document status, the checklist, and every table and dialog. Fourth of the detail page\'s five tabs translated so far; Activity and Workflow remain.',
          es: 'La pestaña de Documentos en la página de detalle de la declaración de impuestos ahora sigue el interruptor de idioma — el estado de los documentos, la lista de verificación y cada tabla y diálogo. Cuarta de las cinco pestañas de la página de detalle traducidas hasta ahora — faltan Actividad y Flujo de Trabajo.',
        },
      },
    ],
  },
  {
    version: '1.15.0',
    date: 'October 26, 2026',
    title: { en: 'Billing & Staff Tab in Spanish', es: 'Pestaña de Facturación y Personal en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Billing & Staff tab on the tax return detail page now follows the language toggle — invoice actions, payment recording, e-signature status, and every dialog and confirmation message. Third of the detail page\'s five tabs translated so far.',
          es: 'La pestaña de Facturación y Personal en la página de detalle de la declaración de impuestos ahora sigue el interruptor de idioma — las acciones de facturación, el registro de pagos, el estado de la firma electrónica y cada diálogo y mensaje de confirmación. Tercera de las cinco pestañas de la página de detalle traducidas hasta ahora.',
        },
      },
    ],
  },
  {
    version: '1.14.0',
    date: 'October 26, 2026',
    title: { en: 'Overview Tab in Spanish', es: 'Pestaña de Resumen en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The Overview tab on the tax return detail page now follows the language toggle — client and spouse info, e-file/signature status, the Dataverse relink tools, and every dialog and status message. First of the detail page\'s five tabs; Documents, Workflow, Billing & Staff, and Activity come next.',
          es: 'La pestaña de Resumen en la página de detalle de la declaración de impuestos ahora sigue el interruptor de idioma — la información del cliente y cónyuge, el estado de presentación electrónica/firma, las herramientas de reenlace de Dataverse y cada diálogo y mensaje de estado. Primera de las cinco pestañas de la página de detalle; Documentos, Flujo de Trabajo, Facturación y Personal, y Actividad vienen después.',
        },
      },
    ],
  },
  {
    version: '1.13.0',
    date: 'October 26, 2026',
    title: { en: 'Tax Return Detail in Spanish, Feedback Fully Connected', es: 'Detalle de Declaración de Impuestos en Español, Comentarios Totalmente Conectados' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'The tax return detail page now follows the language toggle too — header, the status-change dialog, every toolbar button, linked Zoho Sign envelopes, the five tab labels, and every notification toast. The two screens staff use most are now fully bilingual.',
          es: 'La página de detalle de la declaración de impuestos ahora también sigue el interruptor de idioma — el encabezado, el diálogo de cambio de estado, cada botón de la barra de herramientas, los sobres de Zoho Sign vinculados, las cinco pestañas y cada notificación emergente. Las dos pantallas que más usa el personal ya son completamente bilingües.',
        },
      },
      {
        type: 'fix',
        text: {
          en: 'The feedback button fully works now — a report you send reaches the right place and shows up correctly tagged with this app\'s name.',
          es: 'El botón de comentarios ya funciona por completo — un reporte que envíe llega al lugar correcto y aparece correctamente etiquetado con el nombre de esta aplicación.',
        },
      },
    ],
  },
  {
    version: '1.12.0',
    date: 'October 26, 2026',
    title: { en: 'Tax Returns in Spanish', es: 'Declaraciones de Impuestos en Español' },
    changes: [
      {
        type: 'feature',
        text: {
          en: 'If you switch the header toggle to Spanish, the Tax Returns page now actually changes — title, toolbar, every column header, the quick-view buttons, the column chooser, and every dialog and status message. The rest of the app is still English either way; that comes next, screen by screen.',
          es: 'Si cambia el interruptor del encabezado a español, la página de Declaraciones de Impuestos ahora sí cambia — título, barra de herramientas, cada encabezado de columna, los botones de vista rápida, el selector de columnas y cada diálogo y mensaje de estado. El resto de la aplicación sigue en inglés por ahora; eso sigue, pantalla por pantalla.',
        },
      },
      {
        type: 'improvement',
        text: {
          en: 'This page itself (Patch Notes) now responds to the language toggle too — new entries from here on are written in both languages at once, instead of English-only history getting a partial translation later.',
          es: 'Esta misma página (Notas de Versión) ahora también responde al interruptor de idioma — las entradas nuevas a partir de ahora se escriben en ambos idiomas a la vez, en lugar de traducir parcialmente después un historial que solo existía en inglés.',
        },
      },
    ],
  },
  {
    version: '1.11.0',
    date: 'October 22, 2026',
    title: 'Back Button Memory, Date Fixes, One-Click Reassign, and Feedback',
    changes: [
      { type: 'feature', text: 'Going back (or refreshing) no longer resets what you were looking at, on every list page in the app: Tax Returns, Outreach, Priority Outreach, Inquiries, IRS Notices, Workload, Opt-Outs, In-House Clients, Staff Roles, and Drake Import. Filters, sort, active tab, and page number all survive.' },
      { type: 'fix', text: '"On 4/15" and "On or before 4/15" date filters now cover the whole day, instead of quietly excluding anything timestamped later that same day.' },
      { type: 'fix', text: '"Today" now means today in Eastern time, not UTC, for date-token filters like {{today}}.' },
      { type: 'improvement', text: 'The Tax Returns list and other screens\' list filtering now run through one shared engine, so a filter behaves identically everywhere it appears.' },
      { type: 'feature', text: 'One-click reassign: a general upload that never attached to a specific return can now be reassigned to the right one in one click from Client Uploads.' },
      { type: 'feature', text: 'New feedback button in the header — report a problem or suggest an improvement from wherever you are. Shows exactly what it will send before you send it, and gives you a reference number back.' },
      { type: 'feature', text: 'First step toward Spanish: a language toggle in the header. Your choice is remembered and will follow you to another computer. Most screens are still English while we translate them one at a time.' },
    ],
  },
  {
    version: '1.10.0',
    date: 'June 15, 2026',
    title: '1040X Amendments',
    changes: [
      { type: 'feature', text: 'Flag an individual return as a 1040X amendment — an "Amended return (1040X)" toggle now appears in the Add Tax Return and Drop-off dialogs (shown for Individual returns only).' },
      { type: 'feature', text: 'Reclassify an existing individual return as a 1040X amendment from the Edit Return Information dialog.' },
      { type: 'feature', text: 'New Amendment filter on the Tax Returns list (All / 1040X only / Exclude) plus a "1040X" badge next to amended clients.' },
    ],
  },
  {
    version: '1.9.1',
    date: 'May 31, 2026',
    title: 'Priority Outreach Reliability',
    changes: [
      { type: 'improvement', text: 'Priority Outreach loads dramatically faster — Dataverse and opt-out lookups are now batched in parallel (previously ~45s and frequently timing out).' },
      { type: 'improvement', text: 'Server-side pagination on Priority Outreach so large client lists page smoothly.' },
      { type: 'fix', text: 'The Priority Outreach page now surfaces errors and timeouts instead of rendering a silent blank screen.' },
    ],
  },
  {
    version: '1.9.0',
    date: 'May 30, 2026',
    title: 'Multiple Schedule C Businesses',
    changes: [
      { type: 'feature', text: 'A single individual return can now hold multiple Schedule C businesses.' },
      { type: 'feature', text: 'When invoicing, pick the exact entity to bill — the client or a specific Schedule C business — confirmed through the Xero contact dialog so the invoice lands under the right Xero contact.' },
      { type: 'fix', text: 'Single-business billing is now reliable; an explicit entity selection replaces the old name-matching heuristic that could bill the wrong contact.' },
    ],
  },
  {
    version: '1.8.0',
    date: 'May 15, 2026',
    title: 'Priority Outreach, Filing History & Extensions',
    changes: [
      { type: 'feature', text: 'New Priority Outreach page — a prioritized worklist of clients to contact.' },
      { type: 'feature', text: 'Filing History Import — bring prior-year filing history in for reference.' },
      { type: 'feature', text: 'Tax-return extension tracking — record and see at a glance which returns are on extension.' },
    ],
  },
  {
    version: '1.7.4',
    date: 'April 24, 2026',
    title: 'Request Correction Documents',
    changes: [
      { type: 'feature', text: 'New "Request correction docs" button on the return detail page (when status = Needs Corrections) — pick the missing document (1095-A / W-2 / 1099 / Other), add an optional preparer note, choose EN or ES, and send the client an email + SMS carrying a secure single-use upload link.' },
      { type: 'feature', text: '1095-A requests get a detailed how-to-obtain-it guide (insurance agent/broker, Marketplace phone line, and HealthCare.gov step-by-step).' },
      { type: 'improvement', text: 'Correction requests are tracked like outreach, so they appear in history and the inquiry queue; email and SMS each succeed or fail independently.' },
    ],
  },
  {
    version: '1.7.3',
    date: 'April 23, 2026',
    title: 'Link Existing Zoho Sign Envelopes',
    changes: [
      { type: 'feature', text: 'New "Link Zoho Envelope" button on the return detail page — paste a Zoho Sign Request ID (or the admin URL) to link envelopes started outside the app (state 8879s, 529 elections, and other non-templated forms).' },
      { type: 'feature', text: 'Linked envelopes auto-update from Zoho Sign webhooks (Completed / Declined / Expired), and an already-signed envelope pulls its signed PDF into the documents list immediately on link.' },
      { type: 'improvement', text: 'A compact "Linked Zoho Sign envelopes" strip appears on the detail page whenever any are linked.' },
    ],
  },
  {
    version: '1.7.2',
    date: 'April 16, 2026',
    title: 'Invoice Editing & Xero Sync',
    changes: [
      { type: 'feature', text: 'Edit an unpaid invoice\'s amount with the pencil icon on the Billing & Staff tab.' },
      { type: 'feature', text: '"Refresh from Xero" button pulls the latest invoice amount, status, and payment link from Xero — use it after editing an invoice directly in Xero.' },
      { type: 'improvement', text: 'The daily Xero sync now picks up invoice amount changes made directly in Xero, not just payment-status changes.' },
    ],
  },
  {
    version: '1.7.1',
    date: 'April 14, 2026',
    title: 'Client Uploads — Bulk Download, Shift-Select & Uploader Attribution',
    changes: [
      { type: 'feature', text: 'Bulk ZIP download of selected client portal uploads — the server now builds the archive and streams it back, organized into per-client folders (or "General Uploads" for unlinked docs).' },
      { type: 'feature', text: 'Shift-click range selection on both the General Uploads and Return-Linked Uploads tables — click one row, shift-click another to toggle everything in between.' },
      { type: 'feature', text: 'Portal uploads are now attributed to the uploading client via a fallback chain: Dataverse email/phone match first, source tax return\'s client name second (when a client came from a notification link), raw identifier last. No more "Unknown" uploader rows.' },
      { type: 'feature', text: '"Via notification" badge on the Client Uploads page when a general upload came from a notification deep-link, so staff can see which return it was intended for.' },
      { type: 'fix', text: 'ZIP download endpoint now fails loud with detailed error messages when a requested document cannot be fetched, instead of silently returning an empty archive.' },
      { type: 'fix', text: 'Return-Linked Uploads section now correctly shows client name, tax year, upload date, and file size (was previously showing "Unknown client" / "Invalid date" due to a JSON casing bug).' },
    ],
  },
  {
    version: '1.7.0',
    date: 'April 13, 2026',
    title: 'Client Uploads — Tax Return Linking',
    changes: [
      { type: 'feature', text: 'Client Portal Uploads page now splits documents into "General Uploads" (not linked to a return) and "Return-Linked Uploads" with client name and tax year shown inline.' },
      { type: 'feature', text: 'Assign a general upload to a tax return directly from the Client Uploads page — type a client name or tax year and the document is moved into that return\'s Documents tab.' },
      { type: 'feature', text: 'Bulk download selected uploads as a ZIP, organized into per-client folders (or "General Uploads" for unlinked docs).' },
      { type: 'feature', text: 'Deep-link support on the client upload portal — preparer notification emails now auto-select the correct tax return when the client clicks through.' },
      { type: 'improvement', text: 'Reassigning an upload across tax returns now rolls back automatically if the cleanup step fails, preventing duplicate documents across Cosmos partitions.' },
    ],
  },
  {
    version: '1.6.0',
    date: 'April 2, 2026',
    title: 'Tax Clients Page & Xero Contact Resolution',
    changes: [
      { type: 'feature', text: 'New Tax Clients page — view all clients across tax returns with contact details, tax years, return count, and latest status in one place.' },
      { type: 'feature', text: 'Editable Xero Contact ID per client — stored locally in Cosmos DB with audit trail (who changed it and when). Does not sync back to Dataverse.' },
      { type: 'feature', text: 'Stats bar on Tax Clients page showing total clients, individuals, businesses, and Xero ID coverage.' },
      { type: 'improvement', text: 'Xero Contact Verification dialog now resolves the contact from Dataverse first, auto-selecting the linked Xero contact instead of defaulting to "Create new".' },
      { type: 'fix', text: 'Fixed shared invoices not finding existing Xero contacts when multiple Dataverse contacts share the same XeroContactID (e.g., Adri & Dama Installation LLC).' },
    ],
  },
  {
    version: '1.5.0',
    date: 'April 1, 2026',
    title: 'Preparer Notifications & Schedule C Billing',
    changes: [
      { type: 'feature', text: 'Preparers now receive an email notification when a client uploads documents through the portal, with a direct link to the tax return.' },
      { type: 'feature', text: 'New "Bill as Business" option — set the billing name to the Schedule C business so invoices are generated under the correct entity in Xero.' },
      { type: 'feature', text: 'Xero Contact ID auto-resolution from Dataverse — skips the verification dialog when the contact is already known, making invoice generation faster.' },
      { type: 'feature', text: 'Merge Return as Schedule C — merge a business return into an individual return\'s Schedule C, marking the business return as inactive.' },
      { type: 'feature', text: 'Swap Taxpayer & Spouse on joint returns with one click.' },
      { type: 'fix', text: 'Fixed invoices being created under the wrong Xero contact due to fuzzy name matching. Invoices now use the exact Xero Contact ID from Dataverse.' },
      { type: 'fix', text: 'Fixed preparer email not being saved when assigning a preparer through the staff dialog.' },
      { type: 'fix', text: 'Fixed the Create Shared Invoice dialog auto-opening on page load (caused by nested dialog rendering).' },
      { type: 'feature', text: 'Patch Notes page — browse release history with color-coded changes from the nav menu.' },
      { type: 'improvement', text: 'Tax year badge now shown next to client name in the "Other Returns for this Appointment" section.' },
    ],
  },
  {
    version: '1.4.0',
    date: 'March 28, 2026',
    title: 'Staff Roles & Admin Controls',
    changes: [
      { type: 'feature', text: 'Staff Roles page — manage roles (Admin, Preparer, Reviewer, Reception, Staff) for each team member.' },
      { type: 'feature', text: 'Admin-only views — non-admin users see a read-only version of sensitive pages.' },
      { type: 'feature', text: 'Confirmation dialog when removing admin role to prevent accidental lockouts.' },
      { type: 'improvement', text: 'Staff page now supports filters and bulk role assignment.' },
    ],
  },
  {
    version: '1.3.0',
    date: 'March 25, 2026',
    title: 'Drop-Off Workflow & Preparer Pool',
    changes: [
      { type: 'feature', text: 'New "Ready to Prepare" status for drop-off returns that need preparer assignment.' },
      { type: 'feature', text: 'Pool view on the Tax Returns page showing unassigned drop-off returns.' },
      { type: 'improvement', text: 'Drop-off returns auto-advance to "Ready to Prepare" when all documents are received.' },
    ],
  },
  {
    version: '1.2.0',
    date: 'March 20, 2026',
    title: 'Client Opt-Outs & Outreach Tracking',
    changes: [
      { type: 'feature', text: 'Client Opt-Out management page — track and manage clients who opt out of communications.' },
      { type: 'feature', text: 'Opt-out badges shown in Outreach page, preventing selection of opted-out clients.' },
      { type: 'feature', text: 'Added "Clicked" column to outreach history with timestamps on hover.' },
      { type: 'improvement', text: 'Reactivation functionality for previously opted-out clients.' },
    ],
  },
  {
    version: '1.1.0',
    date: 'March 15, 2026',
    title: 'Client Inquiry Queue',
    changes: [
      { type: 'feature', text: 'Inquiries page with stats, filters, and data table for managing client questions.' },
      { type: 'feature', text: 'Inquiry detail panel with message thread and reply composer.' },
      { type: 'feature', text: 'Auto-marks messages as read when viewed.' },
      { type: 'improvement', text: 'Quick reply from list view without opening the full detail panel.' },
    ],
  },
  {
    version: '1.0.0',
    date: 'March 1, 2026',
    title: 'Initial Release',
    changes: [
      { type: 'feature', text: 'Tax return lifecycle management with status tracking and document management.' },
      { type: 'feature', text: 'Client portal with OTP authentication for secure document uploads.' },
      { type: 'feature', text: 'Xero invoice integration — generate, send, and track invoices.' },
      { type: 'feature', text: 'Drake PDF extraction and AI document classification.' },
      { type: 'feature', text: 'E-signature via Zoho Sign integration.' },
      { type: 'feature', text: 'SMS/WhatsApp messaging via Twilio.' },
      { type: 'feature', text: 'Client outreach campaigns with email and SMS.' },
    ],
  },
];

const typeIcons = {
  feature: <NewRegular />,
  fix: <BugRegular />,
  improvement: <WrenchRegular />,
} as const;
const typeColors = {
  feature: { color: '#107c10', bg: '#e6f4e6' },
  fix: { color: '#d83b01', bg: '#fdf2f2' },
  improvement: { color: '#0078d4', bg: '#f0f6ff' },
} as const;

export default function PatchNotesPage() {
  const { t, i18n } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = patchNotes[selectedIndex];

  return (
    <div className="page-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <NoteRegular style={{ fontSize: tokens.fontSizeHero700 }} />
        <Title1>{t('patchNotesPage.title')}</Title1>
        <Badge appearance="filled" color="brand" size="medium">
          {t('patchNotesPage.releaseCount', { count: patchNotes.length })}
        </Badge>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Left sidebar — release list */}
        <div style={{
          width: '260px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
          {patchNotes.map((note, index) => (
            <div
              key={note.version}
              onClick={() => setSelectedIndex(index)}
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                backgroundColor: index === selectedIndex ? '#f0f6ff' : 'transparent',
                border: index === selectedIndex ? '1px solid #0078d4' : '1px solid transparent',
                transition: 'all 0.15s ease',
              }}
              onMouseOver={(e) => {
                if (index !== selectedIndex) e.currentTarget.style.backgroundColor = 'var(--tl-bg-page)';
              }}
              onMouseOut={(e) => {
                if (index !== selectedIndex) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Badge
                  appearance="filled"
                  color={index === 0 ? 'success' : 'informative'}
                  size="small"
                >
                  v{note.version}
                </Badge>
                {index === 0 && (
                  <Badge appearance="outline" color="success" size="small">
                    {t('patchNotesPage.latest')}
                  </Badge>
                )}
              </div>
              <div style={{ fontWeight: index === selectedIndex ? 600 : 400, fontSize: tokens.fontSizeBase300 }}>
                {localizedText(note.title, i18n.language)}
              </div>
              <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginTop: '4px' }}>
                {note.date}
              </div>
            </div>
          ))}
        </div>

        {/* Right content — selected release details */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Card style={{ padding: '24px' }}>
            <CardHeader
              header={
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <RocketRegular style={{ fontSize: tokens.fontSizeBase600, color: '#0078d4' }} />
                    <span data-testid="patch-notes-detail-title" style={{ fontSize: '22px', fontWeight: 600 }}>{localizedText(selected.title, i18n.language)}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Badge appearance="filled" color="brand" size="medium">
                      v{selected.version}
                    </Badge>
                    <span style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>{selected.date}</span>
                  </div>
                </div>
              }
            />

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selected.changes.map((change, i) => {
                const colors = typeColors[change.type];
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px 16px',
                      backgroundColor: colors.bg,
                      borderRadius: '8px',
                      borderLeft: `3px solid ${colors.color}`,
                    }}
                  >
                    <Badge
                      appearance="filled"
                      size="small"
                      style={{ backgroundColor: colors.color, flexShrink: 0, marginTop: '4px' }}
                      icon={typeIcons[change.type]}
                    >
                      {t(`patchNotesPage.types.${change.type}`)}
                    </Badge>
                    <span style={{ fontSize: tokens.fontSizeBase300, lineHeight: '1.5' }}>{localizedText(change.text, i18n.language)}</span>
                  </div>
                );
              })}
            </div>

            {/* Navigation between releases */}
            <Divider style={{ margin: '20px 0 16px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                appearance="subtle"
                icon={<ChevronLeftRegular />}
                disabled={selectedIndex >= patchNotes.length - 1}
                onClick={() => setSelectedIndex(selectedIndex + 1)}
              >
                {t('patchNotesPage.older')}
              </Button>
              <Button
                appearance="subtle"
                icon={<ChevronRightRegular />}
                iconPosition="after"
                disabled={selectedIndex <= 0}
                onClick={() => setSelectedIndex(selectedIndex - 1)}
              >
                {t('patchNotesPage.newer')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
